import { MDBTypography, MDBBtn, MDBIcon, MDBCheckbox } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import ThreeDModelViewer from './ThreeDModelViewer';

/**
 * 3D model file selection + preview component.
 * Does NOT upload — the parent handles upload at submit time.
 *
 * Props:
 * - file3DModel: { fileName } — existing model from backend (edit mode)
 * - pendingFile: File | null — selected but not yet uploaded file
 * - previewUrl: string | null — local blob URL for previewing pendingFile
 * - onFileSelected(file): void — called when user picks a new .glb file
 * - onFileRemoved(): void — called when user removes the model
 */
export default function Product3DModelUpload({
  file3DModel,
  pendingFile,
  previewUrl,
  uploadError,
  conditionChecked,
  conditionError,
  onConditionChange,
  onConditionBlur,
  onFileSelected,
  onFileRemoved,
}) {
  const { t } = useTranslation();

  const existingModelUrl = file3DModel?.fileName
    ? `https://iec-cp-public.s3.eu-central-1.amazonaws.com/Product3DModel/${file3DModel.fileName}`
    : null;

  const hasExistingModel = !!existingModelUrl;
  const showViewer = previewUrl || existingModelUrl;
  const hasModel = hasExistingModel || !!pendingFile;

  const handleSelectFile = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.glb')) {
      return;
    }
    onFileSelected?.(file);
  };

  return (
    <div className="border border-secondary rounded-4 p-4 bg-light my-4">
      <div className="mb-3">
        <MDBTypography tag="h5" className="fw-bold">
          <MDBIcon fas icon="cube" className="me-2 text-primary" />
          {t("Showcase your product in 3D")}
        </MDBTypography>
        <p className="text-muted mb-2">
          {t("Present your product from every angle. With an interactive 3D model, potential customers can explore your product in detail — directly on the platform.")}
        </p>
        <p className="text-muted mb-0">
          {t("You don't have a 3D model yet? No problem — contact us and we will create a professional 3D model for you.")}
        </p>
        <div className="mt-3 text-center">
          <a href={`mailto:info@ib-media.de?subject=${t("3D-ModelRequest")}`} className="btn btn-outline-primary">
            <MDBIcon fas icon="envelope" className="me-2" />
            {t("Request 3D model")}
          </a>
        </div>
      </div>

      {/* 3D Preview */}
      {showViewer && (
        <div className="mb-3">
          <ThreeDModelViewer
            modelUrl={previewUrl || existingModelUrl}
            interactive={true}
            className="p3d-viewer-preview"
          />
          <div className="d-flex align-items-center justify-content-between mt-2 p-2 bg-white rounded border">
            <div>
              <MDBIcon fas icon="cube" className="text-info me-2" />
              <span>{pendingFile?.name || file3DModel?.fileName || t("3D Model")}</span>
            </div>
            <div className="d-flex gap-2">
              <MDBBtn size="sm" color="danger" outline onClick={onFileRemoved}>
                <MDBIcon fas icon="trash" />
              </MDBBtn>
            </div>
          </div>
        </div>
      )}

      {/* File input: show when no model selected */}
      {!hasModel && (
        <>
          <label className="form-control d-flex align-items-center gap-2 text-muted" style={{ cursor: 'pointer' }} htmlFor="file3DModel">
            <MDBIcon fas icon="upload" />
            <span>{t("Choose file")}</span>
            <input
              type="file"
              id="file3DModel"
              className="d-none"
              accept=".glb"
              onChange={e => handleSelectFile(e.target.files[0])}
            />
          </label>
          <div className="alert alert-info mt-3 mb-0" role="alert">
            <MDBIcon fas icon="info-circle" className="me-2" />
            {t("Upload GLB file (.glb)")}
          </div>
        </>
      )}

      {uploadError && (
        <div className="alert alert-danger mt-3 mb-0" role="alert">
          <MDBIcon fas icon="exclamation-triangle" className="me-2" />
          {uploadError}
        </div>
      )}

      {/* Change file when model already exists or is pending */}
      {hasModel && (
        <div className="mt-2">
          <label className="btn btn-sm btn-outline-secondary">
            <MDBIcon fas icon="sync-alt" className="me-1" />
            {t("Change")}
            <input
              type="file"
              accept=".glb"
              className="d-none"
              onChange={e => handleSelectFile(e.target.files[0])}
            />
          </label>
        </div>
      )}

      {/* Condition checkbox */}
      {hasModel && (
        <div className="mt-3">
          <MDBCheckbox
            name="file3DModel-condition"
            id="file3DModel-condition"
            className={conditionError ? "is-invalid" : ""}
            label={t("I hereby confirm that I possess all necessary rights to the uploaded 3D model and that its publication does not infringe any third-party rights. I indemnify Cleanroom-processes.de against all third-party claims.")}
            checked={conditionChecked}
            onChange={(e) => onConditionChange?.("file3DModel", e.target.checked)}
            onBlur={() => onConditionBlur?.('file3DModelCondition')}
          />
          {conditionError && <div className="text-danger small mt-1">{conditionError}</div>}
        </div>
      )}
    </div>
  );
}
